import React, { useState, useMemo, useEffect } from 'react';
import IOSPermissionWizard from '../components/IOSPermissionWizard';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  Lock, 
  Mail, 
  Phone,
  Eye, 
  EyeOff, 
  AlertCircle,
  Sparkles,
  CheckCircle2,
  KeyRound,
  Shield,
  ArrowLeft,
  MailCheck,
  RefreshCw,
  Check
} from 'lucide-react';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');

  // Email verification state
  const [isVerifyingModalOpen, setIsVerifyingModalOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isSubmittingSignup, setIsSubmittingSignup] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const { signup, sendVerificationCode } = useAuth();
  const navigate = useNavigate();

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Password strength calculation
  const strengthInfo = useMemo(() => {
    if (!password) return { score: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password) && /[^A-Za-z0-9]/.test(password)) score += 1;

    if (score <= 1) return { score: 1, label: 'Weak', color: '#ff3b30' };
    if (score === 2) return { score: 2, label: 'Fair', color: '#ff9500' };
    if (score === 3) return { score: 3, label: 'Good', color: '#34c759' };
    return { score: 4, label: 'Strong', color: '#007aff' };
  }, [password]);

  const passwordsMatch = useMemo(() => {
    return confirm && password === confirm;
  }, [password, confirm]);

  // Step 1: Validate inputs and send email verification code
  const handleInitiateSignup = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    const cleanPhone = phone.trim();

    if (!cleanEmail) {
      return setError('Email address is required.');
    }
    if (!cleanPhone) {
      return setError('Phone number is compulsory. Please enter your mobile number.');
    }
    if (cleanPhone.replace(/\D/g, '').length < 7) {
      return setError('Please enter a valid phone number (at least 7 digits).');
    }
    if (password !== confirm) {
      return setError('Passwords do not match');
    }
    if (password.length < 8) {
      return setError('Master password must be at least 8 characters long');
    }

    setError('');
    setIsSendingCode(true);
    try {
      await sendVerificationCode(cleanEmail, cleanPhone);
      setIsVerifyingModalOpen(true);
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send verification code. Please check your email.');
    } finally {
      setIsSendingCode(false);
    }
  };

  // Resend code action
  const handleResendCode = async () => {
    if (resendCooldown > 0 || isSendingCode) return;
    setIsSendingCode(true);
    setError('');
    try {
      await sendVerificationCode(email.trim().toLowerCase(), phone.trim());
      setResendCooldown(60);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code.');
    } finally {
      setIsSendingCode(false);
    }
  };

  // Step 2: Submit verification code and finalize signup
  const handleCompleteSignup = async (e) => {
    e.preventDefault();
    const cleanCode = verificationCode.trim();
    if (!cleanCode) {
      return setError('Please enter the 6-digit verification code.');
    }
    if (cleanCode.length < 4) {
      return setError('Please enter a complete verification code.');
    }

    setError('');
    setIsSubmittingSignup(true);
    try {
      await signup(email.trim().toLowerCase(), password, phone.trim(), cleanCode);
      // Show permission wizard before navigating
      setShowSignupWizard(true);
      setIsVerifyingModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Email verification failed. Please try again.');
    } finally {
      setIsSubmittingSignup(false);
    }
  };

  // Post-signup permission wizard
  const [showSignupWizard, setShowSignupWizard] = useState(false);
  const SIGNUP_PERM_STEPS = [
    {
      id: 'notifications',
      icon: '🔔',
      browserPerm: 'notifications',
      title: '“CipherVault” Would Like to Send You Notifications',
      description: 'For your security: Real-time alerts for unauthorized sign-ins, two-factor authentication (2FA) verification codes, and urgent vault protection notices.',
      detail: 'You can change notification settings anytime.',
      allow: 'Allow',
      deny: "Don't Allow",
    },
    {
      id: 'contacts',
      icon: '👤',
      browserPerm: null,
      title: '“CipherVault” Would Like to Access Your Contacts',
      description: 'For your security: Securely verifies trusted identity contacts and enables encrypted communication with team members.',
      detail: 'Contact information is only used within this app.',
      allow: 'Allow',
      deny: "Don't Allow",
    },
  ];

  return (
    <div className="ios-auth-page">
      {/* Post-signup iOS permission wizard */}
      {showSignupWizard && (
        <IOSPermissionWizard
          steps={SIGNUP_PERM_STEPS}
          onComplete={() => {
            setShowSignupWizard(false);
            navigate('/dashboard');
          }}
        />
      )}

      {/* Ambient backdrop glow */}
      <div className="ios-auth-ambient-glow" />

      <div className="ios-auth-container animate-fade-in">
        <div className="ios-auth-card">
          
          {/* iOS App Icon Header */}
          <div className="ios-auth-header">
            <div className="ios-app-icon-tile">
              <ShieldCheck size={36} className="ios-icon-svg" />
            </div>

            {/* <div className="ios-auth-badge">
              <Sparkles size={12} color="#007AFF" />
              <span>Zero-Knowledge Setup</span>
            </div> */}

            <h1 className="ios-auth-title">Create Vault</h1>
            {/* <p className="ios-auth-subtitle">
              Your master password encrypts all your passwords & data client-side
            </p> */}
          </div>

          {/* Error Banner */}
          {error && (
            <div className="ios-auth-alert-box animate-shake">
              <AlertCircle size={16} className="ios-alert-icon" />
              <span>{error}</span>
            </div>
          )}

          {/* iOS Form */}
          <form onSubmit={handleInitiateSignup} className="ios-auth-form">
            {/* Inset Grouped Card */}
            <div className="ios-input-group">
              {/* Email Row */}
              <div className="ios-input-row">
                <div className="ios-input-icon">
                  <Mail size={18} />
                </div>
                <div className="ios-input-content">
                  <label className="ios-field-label">Email Address *</label>
                  <input
                    type="email"
                    className="ios-native-input"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    inputMode="email"
                    required
                  />
                </div>
              </div>

              <div className="ios-input-divider" />

              {/* Compulsory Phone Number Row */}
              <div className="ios-input-row">
                <div className="ios-input-icon">
                  <Phone size={18} color="#10b981" />
                </div>
                <div className="ios-input-content">
                  <label className="ios-field-label">Phone Number * (Compulsory)</label>
                  <input
                    type="tel"
                    className="ios-native-input"
                    placeholder="e.g. +91 9800000000"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    autoComplete="tel"
                    inputMode="tel"
                    required
                  />
                </div>
              </div>

              <div className="ios-input-divider" />

              {/* Master Password Row */}
              <div className="ios-input-row">
                <div className="ios-input-icon">
                  <Lock size={18} />
                </div>
                <div className="ios-input-content">
                  <label className="ios-field-label">Master Password *</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="ios-native-input"
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
                <button
                  type="button"
                  className="ios-input-action-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="ios-input-divider" />

              {/* Confirm Password Row */}
              <div className="ios-input-row">
                <div className="ios-input-icon">
                  <KeyRound size={18} />
                </div>
                <div className="ios-input-content">
                  <label className="ios-field-label">Confirm Master Password *</label>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    className="ios-native-input"
                    placeholder="Re-enter password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
                <button
                  type="button"
                  className="ios-input-action-btn"
                  onClick={() => setShowConfirm(!showConfirm)}
                  tabIndex={-1}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Password Strength & Match Indicator */}
            {password && (
              <div className="ios-password-strength-box animate-fade-in">
                <div className="ios-strength-header">
                  <span className="ios-strength-label">Password Strength:</span>
                  <span className="ios-strength-val" style={{ color: strengthInfo.color }}>
                    {strengthInfo.label}
                  </span>
                </div>
                <div className="ios-strength-meter">
                  {[1, 2, 3, 4].map((step) => (
                    <div
                      key={step}
                      className="ios-strength-segment"
                      style={{
                        backgroundColor: step <= strengthInfo.score ? strengthInfo.color : undefined
                      }}
                    />
                  ))}
                </div>
                {confirm && (
                  <div className={`ios-match-indicator ${passwordsMatch ? 'matched' : 'mismatch'}`}>
                    {passwordsMatch ? (
                      <>
                        <CheckCircle2 size={13} color="#34c759" />
                        <span>Passwords match</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={13} color="#ff3b30" />
                        <span>Passwords do not match yet</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="ios-btn-primary"
              disabled={isSendingCode || !email.trim() || !phone.trim() || !password.trim() || !confirm.trim()}
            >
              {isSendingCode ? (
                <span className="ios-btn-loading">
                  <span className="ios-spinner" />
                  <span>Sending Verification Code...</span>
                </span>
              ) : (
                <>
                  <MailCheck size={16} />
                  <span>Verify Email & Create Vault</span>
                </>
              )}
            </button>
          </form>

          {/* Switch link */}
          <div className="ios-auth-footer-nav">
            <span className="ios-nav-text">Already have an account?</span>
            <Link to="/login" className="ios-nav-link">
              Log in
            </Link>
          </div>

          {/* Bottom Security Watermark */}
          {/* <div className="ios-auth-security-note">
            <Shield size={13} />
            <span>Never transmitted in plaintext • Client-side encryption</span>
          </div> */}

        </div>
      </div>

      {/* ─────────────────────────────────────────────
          EMAIL VERIFICATION CODE MODAL (Step 2)
          ───────────────────────────────────────────── */}
      {isVerifyingModalOpen && (
        <div className="ios-modal-backdrop animate-fade-in" onClick={() => !isSubmittingSignup && setIsVerifyingModalOpen(false)}>
          <div
            className="ios-modal-card"
            style={{ maxWidth: '440px', padding: 'clamp(16px, 2.5vh, 22px)', margin: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
              <div
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.15)',
                  color: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 8px auto',
                }}
              >
                <MailCheck size={24} />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>Verify Your Email</h3>
              <p style={{ fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: '1.4', margin: 0 }}>
                We sent a 6-digit verification code to <strong style={{ color: 'var(--text-primary)' }}>{email}</strong>
              </p>
              <div style={{ fontSize: '11.5px', color: '#10b981', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                <Phone size={11} />
                <span>Linked mobile: {phone}</span>
              </div>
            </div>

            {/* Error in modal */}
            {error && (
              <div className="ios-auth-alert-box animate-shake" style={{ marginBottom: '12px', padding: '8px 12px' }}>
                <AlertCircle size={14} className="ios-alert-icon" />
                <span style={{ fontSize: '12px' }}>{error}</span>
              </div>
            )}

            {/* Security Email Dispatch Notice */}
            <div
              style={{
                background: 'rgba(59, 130, 246, 0.08)',
                border: '1px solid rgba(59, 130, 246, 0.22)',
                borderRadius: '12px',
                padding: '9px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '14px',
                textAlign: 'left',
              }}
            >
              <ShieldCheck size={16} color="#3b82f6" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.35' }}>
                Please check your email inbox and spam folder for the 6-digit code sent to <strong>{email}</strong>.
              </span>
            </div>

            <form onSubmit={handleCompleteSignup}>
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', textAlign: 'center' }}>
                  Enter 6-Digit Code
                </label>
                <input
                  type="text"
                  maxLength={6}
                  autoFocus
                  className="input-field"
                  style={{
                    textAlign: 'center',
                    fontSize: '22px',
                    fontWeight: '700',
                    letterSpacing: '8px',
                    height: '46px',
                    borderRadius: '12px',
                  }}
                  placeholder="••••••"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  required
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', fontSize: '12px' }}>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || isSendingCode}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: resendCooldown > 0 ? 'var(--text-secondary)' : '#10b981',
                    cursor: resendCooldown > 0 ? 'default' : 'pointer',
                    fontWeight: 600,
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <RefreshCw size={11} className={isSendingCode ? 'spin' : ''} />
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend Code'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setIsVerifyingModalOpen(false);
                    setError('');
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <ArrowLeft size={11} /> Edit Info
                </button>
              </div>

              <button
                type="submit"
                className="ios-btn-primary"
                disabled={isSubmittingSignup || verificationCode.trim().length < 4}
                style={{ width: '100%', height: '42px', background: '#10b981', borderColor: '#10b981' }}
              >
                {isSubmittingSignup ? (
                  <span className="ios-btn-loading">
                    <span className="ios-spinner" />
                    <span>Verifying & Setting Up...</span>
                  </span>
                ) : (
                  <>
                    <Check size={16} />
                    <span>Confirm & Create Vault</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
